import { useState } from 'react';
import { App, Dropdown, type MenuProps } from 'antd';
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  MoreOutlined,
  StarFilled,
  StarOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { ApiRequest } from '@core/entities/ApiRequest';
import { MethodBadge } from '@presentation/components/workbench/MethodBadge';
import { NamePromptModal } from '@presentation/components/workbench/NamePromptModal';
import {
  useDeleteApiRequest,
  useDuplicateApiRequest,
  useRenameApiRequest,
  useToggleApiRequestFavorite,
} from '@presentation/hooks/useApiRequests';
import { useWorkbenchStore } from '@presentation/stores/workbenchStore';
import { container } from '@infrastructure/di/container';
import { QUERY_KEYS } from '@shared/constants';
import { paths } from '@app/routes/paths';

interface RequestTreeNodeProps {
  workspaceId: string;
  request: ApiRequest;
}

export const RequestTreeNode = ({ workspaceId, request }: RequestTreeNodeProps) => {
  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [renaming, setRenaming] = useState(false);
  const [opening, setOpening] = useState(false);
  const openRequestTab = useWorkbenchStore((state) => state.openRequestTab);
  const renameRequest = useRenameApiRequest(request.id);
  const duplicateRequest = useDuplicateApiRequest(request.collectionId, request.folderId);
  const deleteRequest = useDeleteApiRequest(request.collectionId, request.folderId);
  const toggleFavorite = useToggleApiRequestFavorite();

  const handleOpen = async () => {
    navigate(paths.workbench(workspaceId));

    // The tree only holds a summary object (no params/headers/body/scripts —
    // those relations aren't eager-loaded on the collection listing
    // endpoint). An already-open tab already has that data in its draft, so
    // only requests not yet open need a full fetch before opening a tab.
    const alreadyOpen = useWorkbenchStore
      .getState()
      .tabs.some((tab) => tab.requestId === request.id);
    if (alreadyOpen) {
      openRequestTab(workspaceId, request);
      return;
    }

    setOpening(true);
    try {
      const fullRequest = await queryClient.fetchQuery({
        queryKey: QUERY_KEYS.apiRequest(request.id),
        queryFn: () => container.apiRequests.get(request.id),
      });
      openRequestTab(workspaceId, fullRequest);
    } catch (error) {
      void message.error(error instanceof Error ? error.message : 'Failed to open request');
    } finally {
      setOpening(false);
    }
  };

  const menuItems: MenuProps['items'] = [
    {
      key: 'favorite',
      icon: request.isFavorited ? <StarFilled className="text-amber-400" /> : <StarOutlined />,
      label: request.isFavorited ? 'Remove from favorites' : 'Add to favorites',
    },
    { key: 'rename', icon: <EditOutlined />, label: 'Rename' },
    { key: 'duplicate', icon: <CopyOutlined />, label: 'Duplicate' },
    { type: 'divider' },
    { key: 'delete', icon: <DeleteOutlined />, label: 'Delete', danger: true },
  ];

  const handleMenuClick: MenuProps['onClick'] = ({ key, domEvent }) => {
    domEvent.stopPropagation();
    if (key === 'favorite') {
      toggleFavorite.mutate({ id: request.id, isFavorited: request.isFavorited });
    } else if (key === 'rename') {
      setRenaming(true);
    } else if (key === 'duplicate') {
      duplicateRequest.mutate(request.id, {
        onSuccess: () => message.success('Request duplicated'),
      });
    } else if (key === 'delete') {
      modal.confirm({
        title: `Delete "${request.name}"?`,
        content: 'This cannot be undone.',
        okText: 'Delete',
        okButtonProps: { danger: true },
        onOk: () =>
          deleteRequest.mutate(request.id, { onSuccess: () => message.success('Request deleted') }),
      });
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-busy={opening}
      onClick={() => void handleOpen()}
      className="group flex cursor-pointer items-center gap-2 rounded px-2 py-1 pl-8 text-sm hover:bg-black/5 dark:hover:bg-white/5 aria-busy:cursor-wait aria-busy:opacity-60"
    >
      <MethodBadge method={request.method} className="w-10 shrink-0 text-left" />
      <span
        className="min-w-0 flex-1 truncate"
        onDoubleClick={(event) => {
          event.stopPropagation();
          setRenaming(true);
        }}
      >
        {request.name}
      </span>
      {request.isFavorited && <StarFilled className="shrink-0 text-[11px] text-amber-400" />}
      <Dropdown menu={{ items: menuItems, onClick: handleMenuClick }} trigger={['click']}>
        <button
          type="button"
          onClick={(event) => event.stopPropagation()}
          className="shrink-0 rounded px-1 opacity-0 group-hover:opacity-100"
          aria-label="Request actions"
        >
          <MoreOutlined />
        </button>
      </Dropdown>

      <NamePromptModal
        open={renaming}
        title="Rename request"
        initialValue={request.name}
        confirmLoading={renameRequest.isPending}
        onCancel={() => setRenaming(false)}
        onSubmit={(name) =>
          renameRequest.mutate(name, {
            onSuccess: () => setRenaming(false),
            onError: (error) => void message.error(error.message || 'Failed to rename request'),
          })
        }
      />
    </div>
  );
};
